import { useState, useRef, useMemo, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { WizardProgress } from "@/components/WizardProgress";
import { NestedFolderBrowser } from "@/components/NestedFolderBrowser";
import { DraftResumeCard } from "@/components/DraftResumeCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronsDownUp,
  FolderOpen,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  Check,
  Loader2,
  Users,
  ClipboardList,
  X,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, formatDuration } from "@/lib/utils";
import { normalizeStatus, getStatusConfig } from "@/lib/status-config";
import type { SelectedExcelFile, CreateProjectRequest, ParseExcelResponse, UserMappingEntry } from "@/types/api";
import { excelApi } from "@/services/excel";
import { projectsApi } from "@/services/projects";
import { useParseExcelFromSharePoint, useExcelParseProgress } from "@/hooks/useExcel";
import { useCreateProject } from "@/hooks/useProjects";
import { useTesters } from "@/hooks/useTesters";
import { useWizardPersistence } from "@/hooks/useWizardPersistence";
import { batchResolvePersons } from "@/lib/name-matching";
import type { ResolvedPerson } from "@/lib/name-matching";
import type { TesterUser } from "@/services/testers";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const MAX_MAPPING_FILES = 5;
const REVIEW_PAGE_SIZE = 25;
const SCENARIO_PAGE_SIZE = 25;

const wizardSteps = [
  { id: 1, title: "Select Files", description: "Browse & select Excel files" },
  { id: 2, title: "Review Selection", description: "Confirm selected files" },
  { id: 3, title: "Extract Tests", description: "Parse test scenarios" },
  { id: 4, title: "Map Testers", description: "Assign team members" },
];


import type { TesterMapping } from "@/hooks/useWizardPersistence";

// --- Test step display helpers ---

/** Map raw Excel status values to Tailwind badge classes using shared status config */
const getStepStatusColor = (status: string): string => {
  const normalized = normalizeStatus(status);
  return getStatusConfig(normalized).badgeClasses;
};

/** Replace newlines with " | " and truncate for trigger preview */
const formatMultilinePreview = (text: string, maxLen = 80): string => {
  const oneLiner = text.replace(/[\r\n]+/g, " | ").trim();
  return oneLiner.length > maxLen ? oneLiner.slice(0, maxLen) + "..." : oneLiner;
};

/** Strip embedded newlines from column names (e.g. "Scenario\nGroup" → "Scenario Group") */
const cleanColumnName = (name: string): string => name.replace(/[\r\n]+/g, " ").trim();

/** Extract due date from parsed step row and normalize to YYYY-MM-DD when possible */
const getStepDueDate = (step: Record<string, unknown>): string | undefined => {
  const raw =
    step["Due Date"] ??
    step["DUE DATE"] ??
    step["Due date"] ??
    step["due date"] ??
    step["due_date"] ??
    step["dueDate"] ??
    step["Deadline"] ??
    step["Deadline Date"] ??
    step["Due"];

  if (raw == null) return undefined;
  const rawString = String(raw).trim();
  if (!rawString) return undefined;

  const normalized = rawString.replace(/\s+/g, " ");
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  const dmyMatch = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (dmyMatch) {
    const day = dmyMatch[1].padStart(2, "0");
    const month = dmyMatch[2].padStart(2, "0");
    const year = dmyMatch[3].length === 2 ? `20${dmyMatch[3]}` : dmyMatch[3];
    return `${year}-${month}-${day}`;
  }

  return normalized;
};

/** Keys to exclude from expanded step detail (shown in trigger or not useful) */
const TRIGGER_KEYS = new Set(["step", "STEP", "action", "Action", "ACTION", "status", "Status", "STATUS"]);
const COL_AUTO_RE = /^Col_\d+$/i;

/** Return visible key-value pairs for a step (excluding trigger fields, empties, auto-columns) */
const getVisibleFields = (step: Record<string, string>): Array<[string, string]> =>
  Object.entries(step).filter(([key, val]) => {
    if (TRIGGER_KEYS.has(key)) return false;
    if (COL_AUTO_RE.test(key)) return false;
    if (val == null || val === "") return false;
    const strVal = typeof val === "string" ? val : String(val);
    if (strVal.trim() === "" || strVal === "#N/A") return false;
    return true;
  });

import { TesterMappingStep } from "@/components/TesterMappingStep";

const NewProject = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);

  // Ref to prevent double-click race condition
  const isSubmittingRef = useRef(false);

  // Draft persistence
  const { draft, saveDraft, clearDraft, isStale } = useWizardPersistence();
  const [showDraftPrompt, setShowDraftPrompt] = useState(!!draft);
  const [isRestoringDraft, setIsRestoringDraft] = useState(false);
  // Holds the jobId restored from a draft (survives across auto-saves until a new parse runs)
  const [restoredJobId, setRestoredJobId] = useState<string | null>(null);

  // Project creation mutation
  const createProject = useCreateProject();

  // Project info
  const [projectName, setProjectName] = useState("");
  const [projectNumber, setProjectNumber] = useState("");
  const [testingPhase, setTestingPhase] = useState("");
  const [testingPhaseOpen, setTestingPhaseOpen] = useState(false);
  const [projectDeadline, setProjectDeadline] = useState("");
  const [ictResponsible, setIctResponsible] = useState("");

  // Step 1: File selection (using NestedFolderBrowser)
  const [selectedTestFiles, setSelectedTestFiles] = useState<SelectedExcelFile[]>([]);
  const [reviewPage, setReviewPage] = useState(1);
  const [scenarioPage, setScenarioPage] = useState(1);
  const [mappingPage, setMappingPage] = useState(1);

  // Step 1: Mapping file selection (using NestedFolderBrowser)
  const DEFAULT_MAPPING_PATH = import.meta.env.VITE_DEFAULT_MAPPING_PATH || '/';
  const [selectedMappingFile, setSelectedMappingFile] = useState<SelectedExcelFile[]>([]);
  const [isMappingBrowserOpen, setIsMappingBrowserOpen] = useState(true);

  // Check if step 1 is complete
  const isStep1Complete =
    projectName.trim() &&
    projectNumber.trim() &&
    testingPhase &&
    projectDeadline &&
    ictResponsible.trim() &&
    selectedTestFiles.length >= 1 &&
    selectedMappingFile.length >= 1 &&
    selectedMappingFile.length <= MAX_MAPPING_FILES;

  // Step 3: Parse result from backend
  const [parseResult, setParseResult] = useState<ParseExcelResponse | null>(null);
  const [expandedFiles, setExpandedFiles] = useState<string[]>([]);
  const [warningsOpen, setWarningsOpen] = useState(true);
  const [isReparsing, setIsReparsing] = useState(false);

  // Step 4: Tester mapping
  const [testerMappings, setTesterMappings] = useState<TesterMapping[]>([]);
  const [mappingMismatch, setMappingMismatch] = useState(false);
  const [scenarioStepKeysState, setScenarioStepKeysState] = useState<Set<string>>(new Set());
  const [mappingSearch, setMappingSearch] = useState("");
  const [expandedSubdomains, setExpandedSubdomains] = useState<Set<string>>(new Set());
  const [expandedMappingFiles, setExpandedMappingFiles] = useState<Set<string>>(new Set());
  const [isResyncingMappings, setIsResyncingMappings] = useState(false);
  // Ref to store fresh Cognito users during resync (avoids stale closure in useEffect)
  const resyncUsersRef = useRef<TesterUser[] | null>(null);

  // Cognito users — prefetch when reaching step 3 so data is ready for step 4
  const {
    data: testersData,
    isLoading: isTestersLoading,
    isError: isTestersError,
    refetch: refetchTesters,
  } = useTesters({ enabled: currentStep >= 3 });
  const cognitoUsers = testersData?.testers ?? [];

  // Excel parsing hook for test scenarios + mapping file
  const parseExcel = useParseExcelFromSharePoint();
  const { startJob, progress, result: asyncResult, error: asyncError, isPolling, isFetchingResult, isComplete, etaSeconds, jobId: parseJobId } = useExcelParseProgress();
  // Prefer current-session parseJobId, fall back to draft-restored jobId
  const effectiveJobId = parseJobId || restoredJobId;

  const buildFilesToParse = () => [
    ...selectedTestFiles.map(f => ({
      id: f.id,
      path: f.path,
      drive_id: f.driveId || '',
      site_id: f.siteId || '',
      folder_id: f.folderId || '',
      name: f.name,
      type: 'scenario' as const,
      web_url: f.webUrl || '',
    })),
    ...selectedMappingFile.map(f => ({
      id: f.id,
      path: f.path,
      drive_id: f.driveId || '',
      site_id: f.siteId || '',
      folder_id: f.folderId || '',
      name: f.name,
      type: 'mapping' as const,
      web_url: f.webUrl || '',
    })),
  ];

  // --- Draft persistence handlers ---

  const handleResumeDraft = useCallback(async () => {
    if (!draft) return;
    setProjectName(draft.projectName);
    setProjectNumber(draft.projectNumber);
    setTestingPhase(draft.testingPhase);
    setProjectDeadline(draft.projectDeadline);
    setIctResponsible(draft.ictResponsible);
    setSelectedTestFiles(draft.selectedTestFiles);
    setSelectedMappingFile(draft.selectedMappingFile ?? []);
    if ((draft.selectedMappingFile ?? []).length > 0) setIsMappingBrowserOpen(false);
    setScenarioPage(1);
    setTesterMappings(draft.testerMappings);
    if (draft.testerMappings.length > 0) {
      setExpandedMappingFiles(new Set(draft.testerMappings.flatMap(m => m.scenarioFiles ?? [])));
    }
    setShowDraftPrompt(false);

    // Preserve the draft's jobId for future saves (even if S3 restore fails)
    if (draft.jobId) {
      setRestoredJobId(draft.jobId);
    }

    console.log('[Draft Resume]', {
      needsReparse: draft.needsReparse,
      jobId: draft.jobId,
      hasParseResult: !!draft.parseResult,
      scenarioCount: draft.parseResult?.scenarios?.length,
    });

    // If parse data was stripped (or missing) and we have a jobId, restore from S3
    const shouldRestoreFromS3 = !!draft.jobId && (
      draft.needsReparse ||
      !draft.parseResult ||
      !draft.parseResult.scenarios?.[0]?.test_script
    );

    if (shouldRestoreFromS3) {
      setIsRestoringDraft(true);
      setCurrentStep(draft.currentStep);
      try {
        console.log('[Draft Resume] Fetching parse status for job:', draft.jobId);
        const status = await excelApi.getParseStatus(draft.jobId!);
        console.log('[Draft Resume] Parse status:', status.status, 'result_url:', !!status.result_url);
        if (status.status === 'completed' && status.result_url) {
          const fullResult = await excelApi.fetchParseResult(status.result_url);
          console.log('[Draft Resume] Restored', fullResult.scenarios?.length, 'scenarios from S3');
          setParseResult(fullResult);
          setExpandedFiles(fullResult.scenarios.map((_, i) => String(i)));
          toast.success('Draft restored with full parse data.');
        } else {
          // Job no longer completed — fall back to reparse
          setParseResult(draft.parseResult);
          if (draft.parseResult) {
            setExpandedFiles(draft.parseResult.scenarios.map((_, i) => String(i)));
          }
          toast.info('Parse results expired. Click "Reparse" on Step 3 to reload.');
        }
      } catch (err) {
        // S3 fetch failed — fall back to reparse
        console.error('[Draft Resume] S3 restore failed:', err);
        setParseResult(draft.parseResult);
        if (draft.parseResult) {
          setExpandedFiles(draft.parseResult.scenarios.map((_, i) => String(i)));
        }
        toast.info('Could not restore parse details. Click "Reparse" on Step 3 to reload.');
      } finally {
        setIsRestoringDraft(false);
      }
      return;
    }

    // Normal resume — parse data was small enough to save in localStorage
    setParseResult(draft.parseResult);
    setCurrentStep(draft.currentStep);
    if (draft.parseResult) {
      setExpandedFiles(draft.parseResult.scenarios.map((_, i) => String(i)));
    }
    if (draft.needsReparse) {
      toast.info('Parse details were too large to save. Click "Reparse" on Step 3 to reload.');
    }
  }, [draft]);

  const handleDiscardDraft = useCallback(() => {
    clearDraft();
    setShowDraftPrompt(false);
  }, [clearDraft]);

  const handleCancel = useCallback(() => {
    clearDraft();
    navigate('/admin/projects');
  }, [clearDraft, navigate]);

  // Show scroll FABs based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const distanceFromBottom =
        document.documentElement.scrollHeight -
        window.scrollY -
        window.innerHeight;
      setShowScrollButton(distanceFromBottom > 300);
      setShowScrollTopButton(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = useCallback(() => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedFiles([]);
    setWarningsOpen(false);
    setExpandedSubdomains(new Set());
    setExpandedMappingFiles(new Set());
  }, []);

  // Auto-save draft (debounced 500ms)
  useEffect(() => {
    if (showDraftPrompt) return;
    const timer = setTimeout(() => {
      saveDraft({
        currentStep,
        projectName,
        projectNumber,
        testingPhase,
        projectDeadline,
        ictResponsible,
        selectedTestFiles,
        selectedMappingFile,
        parseResult,
        testerMappings,
        jobId: effectiveJobId ?? undefined,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [showDraftPrompt, currentStep, projectName, projectNumber, testingPhase,
      projectDeadline, ictResponsible, selectedTestFiles, selectedMappingFile,
      parseResult, testerMappings, effectiveJobId, saveDraft]);

  // Navigation guard — in-app (manual, since app uses BrowserRouter not data router)
  const hasUnsavedWork = !showDraftPrompt && (currentStep > 1 || projectName.trim() !== '');
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const handleHeaderBack = useCallback(() => {
    if (hasUnsavedWork) {
      setShowLeaveDialog(true);
    } else {
      navigate('/admin/projects');
    }
  }, [hasUnsavedWork, navigate]);

  // Navigation guard — browser close/refresh
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedWork]);

  // When async parse completes, set the parse result.
  // If a resync was in progress (resyncUsersRef is set), also auto-process mappings
  // with the fresh parse data and fresh Cognito users.
  useEffect(() => {
    if (isComplete && asyncResult) {
      setParseResult(asyncResult);
      setScenarioPage(1);
      setExpandedFiles(asyncResult.scenarios.map((_, i) => String(i)));
      if (asyncResult.errors?.length) {
        toast.warning(`${asyncResult.errors.length} file(s) failed to parse`);
      }
      if (asyncResult.warnings?.length) {
        toast.warning(`${asyncResult.warnings.length} workbook sanity warning(s) detected`);
      }
      toast.success(`Parsed ${asyncResult.metadata.files_parsed} file(s): ${asyncResult.metadata.total_steps} steps`);

      // If resync was in progress, process mappings with fresh parse + Cognito data
      if (resyncUsersRef.current) {
        const freshUsers = resyncUsersRef.current;
        resyncUsersRef.current = null;
        setIsResyncingMappings(false);
        processMappings(asyncResult, freshUsers);
        toast.success("Mappings resynced with latest data");
      }
    }
    if (asyncError) {
      toast.error(`Parse failed: ${asyncError}`);
      // Clean up resync state on parse failure
      if (resyncUsersRef.current) {
        resyncUsersRef.current = null;
        setIsResyncingMappings(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, asyncResult, asyncError]);

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!projectName.trim()) {
        toast.error("Please enter a project name");
        return;
      }
      if (!projectNumber.trim()) {
        toast.error("Please enter a project number");
        return;
      }
      if (!/^\d{3}$/.test(projectNumber)) {
        toast.error("Project number must be exactly 3 digits (e.g., 001)");
        return;
      }
      if (!testingPhase) {
        toast.error("Please enter a testing phase");
        return;
      }
      if (!projectDeadline) {
        toast.error("Please select a deadline");
        return;
      }
      if (selectedTestFiles.length === 0) {
        toast.error("Please select at least one test file");
        return;
      }
      if (selectedMappingFile.length === 0) {
        toast.error("Please select a mapping file");
        return;
      }
      // Move directly to step 2 (review selection)
      setReviewPage(1);
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (selectedTestFiles.length > 15) {
        toast.info(`Parsing ${selectedTestFiles.length} files — this will run in the background`);
      }
      setIsLoading(true);
      try {
        // Start async parse job
        const jobResponse = await parseExcel.mutateAsync({ files: buildFilesToParse() });
        // Start polling
        startJob(jobResponse.job_id);
        toast.success(`Parse job started for ${jobResponse.total_files} file(s)`);
        setCurrentStep(3);
      } catch (error) {
        toast.error(`Failed to start parse: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    } else if (currentStep === 3) {
      // Guard: block if Cognito users are still loading
      if (isTestersLoading) {
        toast.info("Loading Cognito users, please wait...");
        return;
      }
      // Warn (but allow) if Cognito user fetch failed — user can retry on Step 4
      if (isTestersError) {
        toast.warning("Cognito user list failed to load. You can retry on the next step.");
      }

      // Process mappings and resolve Cognito users
      processMappings();
      setCurrentStep(4);
    } else if (currentStep === 4) {
      // Prevent double-click
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;

      try {
      // Validate all tester mappings have at least one test person
      const invalidMappings = testerMappings.filter(m => m.testPersons.length === 0);
      if (invalidMappings.length > 0) {
        toast.error(`Please assign at least one test person to each tester role. ${invalidMappings.length} role(s) have no assignments.`);
        return;
      }

      // Validate all persons are resolved to Cognito users
      const unresolvedPersons = testerMappings.flatMap(m =>
        m.testPersons.filter(p => !p.cognitoUserId)
      );
      if (unresolvedPersons.length > 0) {
        toast.error(
          `${unresolvedPersons.length} person(s) not resolved to Cognito users: ${unresolvedPersons.slice(0, 3).map(p => p.excelName).join(', ')}${unresolvedPersons.length > 3 ? '...' : ''}. Please assign a Cognito user to each person.`
        );
        return;
      }

      // Build user_mappings: UserMappingEntry[] grouped by (subdomain/tester, area)
      const mappingMap = new Map<string, UserMappingEntry>();
      testerMappings.forEach(mapping => {
        const subdomain = mapping.tester.trim();
        const area = mapping.area.trim();
        const key = `${subdomain.toLowerCase()}|${area.toLowerCase()}`;

        if (!mappingMap.has(key)) {
          mappingMap.set(key, {
            subdomain,
            area,
            cognito_user_ids: [],
            person_names: [],
            person_emails: [],
          });
        }
        const entry = mappingMap.get(key)!;
        for (const person of mapping.testPersons) {
          if (person.cognitoUserId && !entry.cognito_user_ids.includes(person.cognitoUserId)) {
            entry.cognito_user_ids.push(person.cognitoUserId);
            entry.person_names.push(person.excelName);
            if (person.excelEmail) {
              entry.person_emails!.push(person.excelEmail);
            }
          }
        }
      });
      const userMappings: UserMappingEntry[] = [...mappingMap.values()];

      // Validate: check if Excel Tester+Area combos have matching mappings
      const mappingKeys = new Set(userMappings.map(m =>
        `${m.subdomain.toLowerCase()}|${m.area.toLowerCase()}`
      ));
      const unmappedCombos: string[] = [];
      for (const s of (parseResult?.scenarios ?? [])) {
        for (const step of (s.test_script ?? [])) {
          const tester = (step.Tester || step.TESTER || '').trim();
          const stepArea = (step.Area || step.AREA || '').trim();
          if (tester) {
            const key = `${tester.toLowerCase()}|${stepArea.toLowerCase()}`;
            if (!mappingKeys.has(key) && !unmappedCombos.includes(`${tester} / ${stepArea || '(empty)'}`)) {
              unmappedCombos.push(`${tester} / ${stepArea || '(empty)'}`);
            }
          }
        }
      }
      if (unmappedCombos.length > 0) {
        // Count total unique combos in scenario steps
        const allCombos = new Set<string>();
        for (const s of (parseResult?.scenarios ?? [])) {
          for (const step of (s.test_script ?? [])) {
            const tester = (step.Tester || step.TESTER || '').trim();
            if (tester) {
              const stepArea = (step.Area || step.AREA || '').trim();
              allCombos.add(`${tester.toLowerCase()}|${stepArea.toLowerCase()}`);
            }
          }
        }
        if (unmappedCombos.length >= allCombos.size && allCombos.size > 0) {
          toast.error(
            'No scenario steps have matching user mappings. Fix the mapping file before creating the project.',
            { duration: 8000 }
          );
          return;
        }
        toast.warning(
          `${unmappedCombos.length} tester/area combo(s) in Excel have no mapping: ${unmappedCombos.slice(0, 5).join('; ')}${unmappedCombos.length > 5 ? '...' : ''}. Steps for these won't create test cases.`
        );
      }

      // Check for scenarios without steps
      const allScenarios = parseResult?.scenarios ?? [];
      const skippedNames = allScenarios
        .filter(s => !(s.test_script ?? []).length)
        .map(s => s.project || s.name || s.file_path);

      if (skippedNames.length > 0) {
        toast.warning(
          `${skippedNames.length} scenario(s) skipped (no test steps): ${skippedNames.slice(0, 3).join(', ')}${skippedNames.length > 3 ? '...' : ''}`
        );
      }

      const validCount = allScenarios.length - skippedNames.length;
      if (validCount === 0) {
        toast.error('No scenarios with valid test steps. Cannot create project.');
        return;
      }

      // Validate: no step due_date may exceed the project deadline
      if (projectDeadline) {
        const violations: { scenario: string; step: number; due: string }[] = [];
        for (const s of allScenarios) {
          const scenarioName = s.project || s.name || '';
          for (const step of (s.test_script ?? [])) {
            const dueDate = getStepDueDate(step);
            const stepNum = Number(step.STEP || step.Step || 0);
            if (dueDate && dueDate > projectDeadline) {
              violations.push({ scenario: scenarioName, step: stepNum, due: dueDate });
            }
          }
        }
        if (violations.length > 0) {
          const examples = violations.slice(0, 5)
            .map(v => `${v.scenario} step ${v.step} (${v.due})`)
            .join('; ');
          toast.error(
            `${violations.length} step(s) have due dates after the project deadline (${projectDeadline}): ${examples}${violations.length > 5 ? '...' : ''}`,
            { duration: 8000 }
          );
          return;
        }
      }

      // Prepare project data payload — send job_id instead of scenarios to avoid 413
      const projectData: CreateProjectRequest = {
        name: projectName,
        project_number: projectNumber,
        testing_phase: testingPhase,
        start_date: new Date().toISOString(),
        end_date: projectDeadline,
        go_live_date: projectDeadline,
        ict_responsible: ictResponsible,
        sharepoint_folder: selectedTestFiles.length > 0
          ? selectedTestFiles[0].path.substring(0, selectedTestFiles[0].path.lastIndexOf('/')) || '/'
          : undefined,
        job_id: effectiveJobId || undefined,
        user_mappings: userMappings,
        sharepoint_files: selectedTestFiles.map(f => ({
          id: f.id,
          name: f.name,
          path: f.path,
          drive_id: f.driveId || '',
          web_url: f.webUrl,
        })),
        mapping_files: selectedMappingFile.map(f => ({
          id: f.id,
          name: f.name,
          path: f.path,
          drive_id: f.driveId || '',
          web_url: f.webUrl,
        })),
      };

      // Log payload for debugging (remove in production)
      if (import.meta.env.DEV) {
        console.log('Project creation payload:', projectData);
      }

      // Call backend API
      setIsLoading(true);

      try {
          const result = await createProject.mutateAsync(projectData);

          // If chunks_remaining > 0, continue sending chunks
          if (result.chunks_remaining && result.chunks_remaining > 0 && result.next_offset) {
            let offset = result.next_offset;
            let remaining = result.chunks_remaining;
            let totalSteps = result.total_steps;
            const totalScenarios = result.scenario_count;
            let scenariosProcessed = offset;

            toast.info(`Creating project: ${scenariosProcessed} / ${totalScenarios} scenarios...`);

            while (remaining > 0) {

              const chunkResult = await projectsApi.continueCreation({
                _continue_project_id: result.project_id,
                _next_offset: offset,
                job_id: projectData.job_id!,
                name: projectData.name,
                project_number: projectData.project_number,
                testing_phase: projectData.testing_phase,
                ict_responsible: projectData.ict_responsible,
                user_mappings: projectData.user_mappings,
                start_date: projectData.start_date,
                end_date: projectData.end_date,
                sharepoint_files: projectData.sharepoint_files,
              });

              offset = chunkResult.next_offset;
              remaining = chunkResult.chunks_remaining;
              totalSteps += chunkResult.chunk_steps;
              scenariosProcessed = offset;
              toast.info(`Creating project: ${scenariosProcessed} / ${totalScenarios} scenarios...`);
            }

            clearDraft();
            toast.success(
              `Project "${result.name}" created with ${result.scenario_count} scenarios and ${totalSteps} steps!`
            );
            navigate(`/admin/projects/${result.project_id}/report`);
          } else {
            clearDraft();
            toast.success(
              `Project "${result.name}" created with ${result.scenario_count} scenarios and ${result.total_steps} steps!`
            );
            navigate(`/admin/projects/${result.project_id}/report`);
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          toast.error(`Failed to create project: ${message}`);
        }
      } finally {
        isSubmittingRef.current = false;
        setIsLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReparse = async () => {
    if (parseExcel.isPending || isReparsing) return;
    setIsReparsing(true);
    try {
      const jobResponse = await parseExcel.mutateAsync({ files: buildFilesToParse() });
      startJob(jobResponse.job_id);
      setParseResult(null);  // Clear old results while new parse runs
      setScenarioPage(1);
    } catch (error) {
      toast.error(`Reparse failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsReparsing(false);
    }
  };

  const toggleFileExpansion = (fileId: string) => {
    setExpandedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const addTestPerson = (mappingId: string, person: ResolvedPerson) => {
    if (!person.excelName.trim()) return;
    setTesterMappings(prev =>
      prev.map(m => {
        if (m.id !== mappingId) return m;
        if (m.testPersons.some(p => p.cognitoUserId === person.cognitoUserId && person.cognitoUserId)) return m;
        return { ...m, testPersons: [...m.testPersons, person] };
      })
    );
  };

  const removeTestPerson = (mappingId: string, personIndex: number) => {
    setTesterMappings(prev =>
      prev.map(m => {
        if (m.id !== mappingId) return m;
        return { ...m, testPersons: m.testPersons.filter((_, i) => i !== personIndex) };
      })
    );
  };

  const handleResolve = (mappingId: string, personIndex: number, cognitoUser: TesterUser) => {
    setTesterMappings(prev =>
      prev.map(m => {
        if (m.id !== mappingId) return m;
        const updated = [...m.testPersons];
        updated[personIndex] = {
          ...updated[personIndex],
          cognitoUserId: cognitoUser.id,
          cognitoUserName: cognitoUser.name,
          cognitoEmail: cognitoUser.email,
          matchConfidence: 'manual',
        };
        return { ...m, testPersons: updated };
      })
    );
  };

  const handleAddCognitoUser = (mappingId: string, user: TesterUser) => {
    const resolved: ResolvedPerson = {
      excelName: user.name,
      excelEmail: null,
      cognitoUserId: user.id,
      cognitoUserName: user.name,
      cognitoEmail: user.email,
      matchConfidence: 'manual',
    };
    addTestPerson(mappingId, resolved);
  };

  // Assign a single Cognito user to every unresolved person across all mappings.
  const handleAssignToAll = (user: TesterUser) => {
    let assignedCount = 0;
    setTesterMappings(prev => {
      const next = prev.map(m => ({
        ...m,
        testPersons: m.testPersons.map(p => {
          if (p.cognitoUserId) return p; // already resolved — leave untouched
          assignedCount++;
          return {
            ...p,
            cognitoUserId: user.id,
            cognitoUserName: user.name,
            cognitoEmail: user.email,
            matchConfidence: 'manual' as const,
          };
        }),
      }));
      return next;
    });
    toast.success(`Assigned ${user.name} to all unresolved tester roles`);
  };

  // Process mappings from parseResult, resolve Cognito users, and update testerMappings state.
  // This is extracted from handleNext step 3→4 transition so it can be reused by the resync button.
  const processMappings = useCallback((
    overrideParseResult?: ParseExcelResponse | null,
    overrideCognitoUsers?: TesterUser[]
  ) => {
    const effectiveParseResult = overrideParseResult ?? parseResult;
    const effectiveCognitoUsers = overrideCognitoUsers ?? cognitoUsers;

    // Use mappings from parsed SharePoint files
    const mappingsData = (effectiveParseResult?.user_mappings ?? []) as Array<Record<string, string>>;

    // DEBUG: log raw mapping data from backend
    if (import.meta.env.DEV) {
      console.group('[Step 3→4] Mapping debug');
      console.log('mappingsData count:', mappingsData.length);
      console.log('mappingsData sample (first 3):', mappingsData.slice(0, 3));
    }

    if (mappingsData.length === 0) {
      toast.warning("No user mappings found. Proceeding with empty mappings.");
    }

    // Build a map from (tester|area) key → Set of scenario file names that use it.
    // This drives both the mapping filter and the per-file grouping in Step 4.
    // scenarioName uses scenario.name (= file_name from backend), which matches
    // selectedTestFiles[i].name — both are the bare filename (e.g. "AP_Finance.xlsx").
    const scenarioStepToFiles = new Map<string, Set<string>>();
    for (const scenario of (effectiveParseResult?.scenarios ?? [])) {
      // Prefer the bare filename; fall back to basename of file_path
      const scenarioName = scenario.name
        || (scenario.file_path ? scenario.file_path.split('/').pop() || '' : '')
        || '';
      if (!scenarioName) continue;
      for (const step of (scenario.test_script ?? [])) {
        const tester = (step.Tester || step.TESTER || '').trim();
        const area = (step.Area || step.AREA || '').trim();
        if (tester) {
          const key = `${tester.toLowerCase()}|${area.toLowerCase()}`;
          if (!scenarioStepToFiles.has(key)) scenarioStepToFiles.set(key, new Set());
          scenarioStepToFiles.get(key)!.add(scenarioName);
        }
      }
    }
    const scenarioStepKeys = new Set(scenarioStepToFiles.keys());

    // DEBUG: log scenario keys and mapping keys
    if (import.meta.env.DEV) {
      console.log('scenarioStepKeys:', [...scenarioStepKeys]);
      const mappingKeys = mappingsData.map(row => {
        const t = (row.TESTER || '').trim();
        const a = (row.AREA || '').trim();
        return `${t.toLowerCase()}|${a.toLowerCase()}`;
      });
      console.log('mappingKeys (unique):', [...new Set(mappingKeys)]);
    }

    // Filter mappings to only include combos found in scenario steps.
    // If filtering removes ALL rows, fall back to showing all mappings
    // so the user can still configure tester assignments.
    const originalCount = mappingsData.length;
    let filteredMappingsData: Array<Record<string, string>>;
    if (scenarioStepKeys.size > 0) {
      const matched = mappingsData.filter(row => {
        const tester = (row.TESTER || '').trim();
        const area = (row.AREA || '').trim();
        if (!tester) return false;
        return scenarioStepKeys.has(`${tester.toLowerCase()}|${area.toLowerCase()}`);
      });
      if (matched.length > 0) {
        filteredMappingsData = matched;
        setMappingMismatch(false);
      } else {
        // Check if swapping TESTER↔AREA would produce matches (common mistake)
        const swappedKeys = new Set(mappingsData.map(r => {
          const t = (r.AREA || '').trim();
          const a = (r.TESTER || '').trim();
          return `${t.toLowerCase()}|${a.toLowerCase()}`;
        }));
        const swapMatches = [...scenarioStepKeys].some(k => swappedKeys.has(k));

        if (swapMatches) {
          toast.error(
            'TESTER and Area columns appear swapped in the mapping file. Check that TESTER contains the tester name (e.g. "AP") and Area contains the location (e.g. "Briec").',
            { duration: 10000 }
          );
        } else {
          toast.error(
            'No mapping rows match any scenario step (Tester + Area). Check that mapping values match the test script\'s Tester and Area columns.',
            { duration: 10000 }
          );
        }
        if (import.meta.env.DEV) {
          console.warn(
            '[Step 3→4] No mapping rows match scenario step (tester|area) keys.',
            { scenarioKeys: [...scenarioStepKeys], mappingKeys: [...new Set(mappingsData.map(r => `${(r.TESTER || '').toLowerCase()}|${(r.AREA || '').toLowerCase()}`))] }
          );
        }
        // Show mapping data so user can see the problem, but flag as invalid
        filteredMappingsData = mappingsData;
        setMappingMismatch(true);
      }
    } else {
      filteredMappingsData = mappingsData;
      setMappingMismatch(false);
    }
    setScenarioStepKeysState(scenarioStepKeys);
    const removedCount = originalCount - filteredMappingsData.length;
    if (import.meta.env.DEV) {
      console.log('filteredMappingsData count:', filteredMappingsData.length, '(removed:', removedCount, ')');
      console.groupEnd();
    }

    // Collect all unique person names + emails across all rows for batch resolution
    const allPersons: Array<{ name: string; email?: string | null }> = [];
    for (const row of filteredMappingsData) {
      const person = row.TESTPERSON;
      if (person) allPersons.push({ name: person, email: row.EMAIL || row.KU_MAILS || null });
    }

    // Auto-resolve person names to Cognito users (email match takes priority)
    const resolutionMap = batchResolvePersons(allPersons, effectiveCognitoUsers);

    // Union-merge rows by (tester, area) key so multiple mapping files
    // contributing to the same role are combined into one TesterMapping entry.
    const mergeMap = new Map<string, TesterMapping>();
    let mergeIndex = 0;

    for (const row of filteredMappingsData) {
      const tester = row.TESTER || '';
      const area = row.AREA || '';
      const key = `${tester.toLowerCase()}|${area.toLowerCase()}`;

      if (!mergeMap.has(key)) {
        mergeIndex += 1;
        mergeMap.set(key, {
          id: String(mergeIndex),
          domain: row.Domain || '',
          tester,
          area,
          testPersons: [],
          ...(row.Subdomain ? { subdomain: row.Subdomain } : {}),
          scenarioFiles: scenarioStepToFiles.has(key)
            ? [...scenarioStepToFiles.get(key)!]
            : [],
        });
      }

      const entry = mergeMap.get(key)!;
      const personName = row.TESTPERSON || '';
      if (personName) {
        // Only add the person if not already present (union deduplication)
        const alreadyPresent = entry.testPersons.some(
          p => p.excelName.toLowerCase() === personName.toLowerCase()
        );
        if (!alreadyPresent) {
          const resolved = resolutionMap.get(personName);
          entry.testPersons.push(
            resolved ?? {
              excelName: personName,
              excelEmail: row.EMAIL || row.KU_MAILS || null,
              cognitoUserId: null,
              cognitoUserName: null,
              cognitoEmail: null,
              matchConfidence: null,
            }
          );
        }
      }
    }

    const mappings: TesterMapping[] = [...mergeMap.values()];

    setTesterMappings(mappings);
    setMappingSearch("");
    setMappingPage(1);
    // Default all mapping file groups to expanded
    setExpandedMappingFiles(new Set(mappings.flatMap(m => m.scenarioFiles ?? [])));

    // Filtering feedback
    if (removedCount > 0) {
      toast.info(
        `Filtered ${removedCount} mapping row(s) not in test scenarios. Using ${filteredMappingsData.length} relevant row(s) → ${mappings.length} unique role(s).`,
        { duration: 5000 }
      );
    } else if (filteredMappingsData.length === 0 && originalCount > 0) {
      toast.warning("All user mappings were filtered out — no matching tester/area combos in scenarios.");
    }

    // Multi-file merge feedback
    const mergedRoleCount = mappings.length;
    const sourceFileCount = selectedMappingFile.length;
    if (sourceFileCount > 1 && mergedRoleCount < filteredMappingsData.length) {
      toast.info(
        `Merged ${filteredMappingsData.length} rows from ${sourceFileCount} mapping files into ${mergedRoleCount} unique role(s).`,
        { duration: 5000 }
      );
    }

    // Resolution summary toast
    const totalPersons = allPersons.length;
    const exactCount = [...resolutionMap.values()].filter(r => r.matchConfidence === 'exact').length;
    const fuzzyCount = [...resolutionMap.values()].filter(r => r.matchConfidence === 'fuzzy').length;
    const unresolvedCount = [...resolutionMap.values()].filter(r => r.matchConfidence === null).length;

    if (totalPersons > 0) {
      const parts: string[] = [];
      if (exactCount > 0) parts.push(`${exactCount} exact`);
      if (fuzzyCount > 0) parts.push(`${fuzzyCount} fuzzy`);
      if (unresolvedCount > 0) parts.push(`${unresolvedCount} unresolved`);
      toast.success(`Resolved ${totalPersons} person(s): ${parts.join(', ')}`);
    } else if (mappings.length > 0) {
      toast.success(`Loaded ${mappings.length} tester role(s) from ${sourceFileCount} mapping file(s)`);
    }
  }, [parseResult, cognitoUsers, selectedMappingFile]);

  // Resync mappings — refetch Cognito users, reparse Excel files, then reprocess mappings.
  // The reparse is async (polling-based), so we store fresh Cognito users in a ref.
  // When the parse completes, the parse-completion useEffect detects the ref and
  // auto-calls processMappings with fresh data.
  const handleResyncMappings = useCallback(async () => {
    if (isResyncingMappings) return;
    setIsResyncingMappings(true);
    try {
      // 1. Refetch Cognito users and store fresh list in ref
      const testersResult = await refetchTesters();
      resyncUsersRef.current = testersResult.data?.testers ?? [];

      // 2. Trigger Excel reparse (same as handleReparse)
      const jobResponse = await parseExcel.mutateAsync({ files: buildFilesToParse() });
      startJob(jobResponse.job_id);
      setParseResult(null);  // Clear stale parse data so auto-save doesn't persist old due dates
      setScenarioPage(1);
      toast.info("Reparsing Excel files and refreshing mappings...");
      // Parse completion will be handled by the useEffect that watches isComplete
    } catch {
      toast.error("Failed to resync mappings");
      setIsResyncingMappings(false);
      resyncUsersRef.current = null;
    }
  }, [isResyncingMappings, refetchTesters, parseExcel, startJob]);

  const handleSubdomainToggle = useCallback((key: string, open: boolean) => {
    setExpandedSubdomains(prev => {
      const next = new Set(prev);
      if (open) next.add(key); else next.delete(key);
      return next;
    });
  }, []);

  const handleFileToggle = useCallback((file: string, open: boolean) => {
    setExpandedMappingFiles(prev => {
      const next = new Set(prev);
      if (open) next.add(file); else next.delete(file);
      return next;
    });
  }, []);

  // Calculate totals for step 3 from parse result
  const totalScenarios = parseResult?.metadata.total_scenarios ?? 0;
  const totalSteps = parseResult?.metadata.total_steps ?? 0;
  const uniqueTesters = new Set((parseResult?.scenarios ?? []).flatMap(s => s.testers ?? []));
  const totalTeams = uniqueTesters.size;

  // Analyze non-new statuses across all scenarios for warning display
  const nonNewAnalysis = useMemo(() => {
    const scenarios = parseResult?.scenarios ?? [];
    const scenarioDetails: Array<{
      nonNewCount: number;
      totalSteps: number;
      byStatus: Record<string, number>;
    } | null> = [];
    let totalNonNew = 0;
    const affectedScenarios: Array<{
      index: number;
      scenarioName: string;
      nonNewCount: number;
      totalSteps: number;
      byStatus: Record<string, number>;
    }> = [];

    scenarios.forEach((scenario, index) => {
      const steps = scenario.test_script ?? [];
      const byStatus: Record<string, number> = {};
      let nonNewCount = 0;

      for (const step of steps) {
        const rawStatus = step.Status || step.STATUS || step.status || '';
        const normalized = normalizeStatus(rawStatus);
        if (normalized !== 'new') {
          nonNewCount++;
          byStatus[normalized] = (byStatus[normalized] ?? 0) + 1;
        }
      }

      if (nonNewCount > 0) {
        const detail = {
          index,
          scenarioName: scenario.name,
          nonNewCount,
          totalSteps: steps.length,
          byStatus,
        };
        scenarioDetails.push(detail);
        affectedScenarios.push(detail);
        totalNonNew += nonNewCount;
      } else {
        scenarioDetails.push(null);
      }
    });

    return { totalNonNew, affectedScenarios, scenarioDetails };
  }, [parseResult]);

  // Sort scenarios: non-new statuses first, then warnings, then rest.
  // Within each group, sort by file modified date descending.
  const sortedScenarios = useMemo(() => {
    const scenarios = parseResult?.scenarios ?? [];
    if (scenarios.length === 0) return [];

    // Build filename → modifiedAt lookup from selected files
    const modifiedMap = new Map<string, string>();
    for (const f of selectedTestFiles) {
      if (f.modifiedAt) modifiedMap.set(f.name, f.modifiedAt);
    }

    const items = scenarios.map((scenario, originalIndex) => {
      const hasNonNew = nonNewAnalysis.scenarioDetails[originalIndex] !== null;
      const hasWarnings = (scenario.warnings?.length ?? 0) > 0;
      // Priority: 0 = non-new statuses, 1 = warnings, 2 = normal
      const priority = hasNonNew ? 0 : hasWarnings ? 1 : 2;
      const modifiedAt = modifiedMap.get(scenario.name) || '';
      return { scenario, originalIndex, priority, modifiedAt };
    });

    items.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      // Within same priority: most recent first, items without dates last
      if (a.modifiedAt && b.modifiedAt) return b.modifiedAt.localeCompare(a.modifiedAt);
      if (a.modifiedAt) return -1;
      if (b.modifiedAt) return 1;
      return 0;
    });

    return items;
  }, [parseResult, nonNewAnalysis, selectedTestFiles]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        title={projectName.trim() || "New Project"}
        showBack
        onBack={handleHeaderBack}
      />

      <main className="flex-1 px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Wizard Progress */}
          <WizardProgress steps={wizardSteps} currentStep={currentStep} />

          {/* Draft Resume Prompt */}
          {showDraftPrompt && draft ? (
            <DraftResumeCard
              draft={draft}
              isStale={isStale}
              onResume={handleResumeDraft}
              onDiscard={handleDiscardDraft}
            />
          ) : isRestoringDraft ? (
            <Card className="animate-fade-in">
              <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Restoring project data...</p>
              </CardContent>
            </Card>
          ) : (
          /* Step Content */
          <Card className="animate-fade-in">
            {/* Step 1: Select Excel Files */}
            {currentStep === 1 && (
              <>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-display">
                    <FolderOpen className="h-5 w-5 text-primary" />
                    Project Setup
                  </CardTitle>
                  <CardDescription>
                    Enter project details and browse SharePoint to select files
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Project Info Section */}
                  <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/30">
                    {/* Row 1: 3 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Project Name */}
                      <div className="space-y-2">
                        <Label htmlFor="project-name" className="text-sm font-medium">
                          Project Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="project-name"
                          placeholder="e.g., SAP S4 Migration"
                          value={projectName}
                          onChange={(e) => setProjectName(e.target.value)}
                          className="bg-background"
                        />
                      </div>

                      {/* Project Number */}
                      <div className="space-y-2">
                        <Label htmlFor="project-number" className="text-sm font-medium">
                          Project Number <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="project-number"
                          placeholder="e.g., 001"
                          maxLength={3}
                          pattern="[0-9]{3}"
                          value={projectNumber}
                          onChange={(e) => setProjectNumber(e.target.value.replace(/\D/g, '').slice(0, 3))}
                          className="bg-background"
                        />
                      </div>

                      {/* Testing Phase */}
                      <div className="space-y-2">
                        <Label htmlFor="testing-phase" className="text-sm font-medium">
                          Testing Phase <span className="text-destructive">*</span>
                        </Label>
                        <Popover open={testingPhaseOpen} onOpenChange={setTestingPhaseOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={testingPhaseOpen}
                              className={cn(
                                "w-full justify-between bg-background font-normal",
                                !testingPhase && "text-muted-foreground"
                              )}
                            >
                              {testingPhase || "e.g., UAT, SIT, Regression"}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command>
                              <CommandInput
                                placeholder="Type or select a phase..."
                                value={testingPhase}
                                onValueChange={setTestingPhase}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && testingPhase) {
                                    e.preventDefault();
                                    setTestingPhaseOpen(false);
                                  }
                                }}
                              />
                              <CommandList>
                                <CommandEmpty>
                                  Press enter to use &quot;{testingPhase}&quot;
                                </CommandEmpty>
                                <CommandGroup>
                                  {["UAT", "SIT", "Regression", "Integration", "Smoke", "Performance", "Security", "E2E", "Acceptance"].map((phase) => (
                                    <CommandItem
                                      key={phase}
                                      value={phase}
                                      onSelect={(value) => {
                                        setTestingPhase(value);
                                        setTestingPhaseOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          testingPhase.toLowerCase() === phase.toLowerCase() ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      {phase}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    {/* Row 2: 2 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Deadline */}
                      <div className="space-y-2">
                        <Label htmlFor="project-deadline" className="text-sm font-medium">
                          Deadline <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="project-deadline"
                          type="date"
                          value={projectDeadline}
                          onChange={(e) => setProjectDeadline(e.target.value)}
                          className="bg-background"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>

                      {/* ICT Responsible */}
                      <div className="space-y-2">
                        <Label htmlFor="ict-responsible" className="text-sm font-medium">
                          ICT Responsible <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="ict-responsible"
                          placeholder="e.g., Jan De Smedt"
                          value={ictResponsible}
                          onChange={(e) => setIctResponsible(e.target.value)}
                          className="bg-background"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Test Files Selection */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-muted-foreground">
                        Test Files <span className="text-destructive">*</span>
                      </h3>
                      {selectedTestFiles.length > 0 && (
                        <Badge variant="secondary">{selectedTestFiles.length} selected</Badge>
                      )}
                    </div>
                    <NestedFolderBrowser
                      initialPath="/"
                      onSelectionChange={setSelectedTestFiles}
                      multiSelect={true}
                      showSummary={true}
                      title="Browse Test Files"
                      description="Select Excel files or use 'Select All' to import an entire folder"
                      allowSelectAll={true}
                      allowRecursiveSelect={true}
                    />
                  </div>

                  {/* Mapping File(s) Selection */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-muted-foreground">
                        Mapping File(s) <span className="text-destructive">*</span>
                        <span className="ml-1 text-xs font-normal text-muted-foreground/70">(up to {MAX_MAPPING_FILES})</span>
                      </h3>
                      {selectedMappingFile.length > 0 && (
                        <Badge variant="secondary">{selectedMappingFile.length} selected</Badge>
                      )}
                    </div>

                    {/* Compact summary when files are selected and browser is closed */}
                    {selectedMappingFile.length > 0 && !isMappingBrowserOpen && (
                      <div className="space-y-2">
                        {selectedMappingFile.length === 1 ? (
                          <div className="flex items-center gap-4 p-4 rounded-lg bg-success/5 border border-success/20">
                            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                              <Users className="h-5 w-5 text-success" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{selectedMappingFile[0].name}</p>
                              <p className="text-xs text-muted-foreground truncate">{selectedMappingFile[0].path}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsMappingBrowserOpen(true)}
                            >
                              Change
                            </Button>
                          </div>
                        ) : (
                          <div className="rounded-lg border border-success/20 bg-success/5 overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-success/10">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-success" />
                                <span className="text-sm font-medium">{selectedMappingFile.length} mapping files selected</span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsMappingBrowserOpen(true)}
                              >
                                Change
                              </Button>
                            </div>
                            <div className="divide-y divide-border/50">
                              {selectedMappingFile.map((file, idx) => (
                                <div key={file.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                                  <span className="text-xs text-muted-foreground w-5 shrink-0">{idx + 1}.</span>
                                  <span className="font-medium truncate flex-1">{file.name}</span>
                                  <span className="text-xs text-muted-foreground truncate max-w-[200px]">{file.path}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* File browser when no files selected or when "Change" clicked */}
                    {(selectedMappingFile.length === 0 || isMappingBrowserOpen) && (
                      <NestedFolderBrowser
                        initialPath={DEFAULT_MAPPING_PATH}
                        onSelectionChange={(files) => {
                          setSelectedMappingFile(files);
                          if (files.length > 0) setIsMappingBrowserOpen(false);
                        }}
                        multiSelect={true}
                        maxSelections={MAX_MAPPING_FILES}
                        showSummary={true}
                        title="Browse Mapping Files"
                        description={`Select up to ${MAX_MAPPING_FILES} User Mapping Excel files`}
                      />
                    )}
                  </div>
                </CardContent>
              </>
            )}

            {/* Step 2: Review Selection */}
            {currentStep === 2 && (
              <>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-display">
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                    Review Selected Files
                  </CardTitle>
                  <CardDescription>
                    Confirm your selection before proceeding to extract test scenarios
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Test Files (paginated) */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">
                        Test Files ({selectedTestFiles.length})
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentStep(1)}
                      >
                        Edit Selection
                      </Button>
                    </div>
                    {(() => {
                      const totalPages = Math.ceil(selectedTestFiles.length / REVIEW_PAGE_SIZE);
                      const safePage = Math.min(reviewPage, totalPages || 1);
                      const startIdx = (safePage - 1) * REVIEW_PAGE_SIZE;
                      const pageFiles = selectedTestFiles.slice(startIdx, startIdx + REVIEW_PAGE_SIZE);

                      return (
                        <>
                          <div className="space-y-2">
                            {pageFiles.map((file) => (
                              <div
                                key={file.id}
                                className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 border border-border"
                              >
                                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                  <FileSpreadsheet className="h-5 w-5 text-success" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{file.name}</p>
                                  <p className="text-sm text-muted-foreground truncate">{file.path}</p>
                                </div>
                                <Badge variant="secondary">
                                  {(file.size / 1024).toFixed(0)} KB
                                </Badge>
                              </div>
                            ))}
                          </div>
                          {totalPages > 1 && (
                            <Pagination>
                              <PaginationContent>
                                <PaginationItem>
                                  <PaginationPrevious
                                    onClick={() => setReviewPage(p => Math.max(1, p - 1))}
                                    className={cn(safePage <= 1 && "pointer-events-none opacity-50")}
                                  />
                                </PaginationItem>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                                  .map((p, idx, arr) => (
                                    <Fragment key={p}>
                                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                                        <PaginationItem><PaginationEllipsis /></PaginationItem>
                                      )}
                                      <PaginationItem>
                                        <PaginationLink
                                          isActive={p === safePage}
                                          onClick={() => setReviewPage(p)}
                                        >
                                          {p}
                                        </PaginationLink>
                                      </PaginationItem>
                                    </Fragment>
                                  ))}
                                <PaginationItem>
                                  <PaginationNext
                                    onClick={() => setReviewPage(p => Math.min(totalPages, p + 1))}
                                    className={cn(safePage >= totalPages && "pointer-events-none opacity-50")}
                                  />
                                </PaginationItem>
                              </PaginationContent>
                            </Pagination>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Mapping File(s) */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">
                        Mapping File(s) ({selectedMappingFile.length})
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {selectedMappingFile.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 border border-border"
                        >
                          <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                            <Users className="h-5 w-5 text-success" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{file.name}</p>
                            <p className="text-sm text-muted-foreground truncate">{file.path}</p>
                          </div>
                          <Badge variant="secondary">
                            {(file.size / 1024).toFixed(0)} KB
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </>
            )}

            {/* Step 3: Extract Test Steps */}
            {currentStep === 3 && (
              <>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 font-display">
                        <ClipboardList className="h-5 w-5 text-primary" />
                        Extracted Test Scenarios
                      </CardTitle>
                      <CardDescription>
                        Review the test scenarios and steps extracted from your Excel files
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReparse}
                      disabled={isReparsing}
                      className="gap-2 shrink-0"
                    >
                      <RefreshCw className={cn("h-4 w-4", isReparsing && "animate-spin")} />
                      {isReparsing ? "Reparsing..." : "Reparse"}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Async parsing progress */}
                  {isPolling && (
                    <div className="space-y-4 p-6 rounded-lg border border-border bg-muted/30">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {isFetchingResult
                            ? 'Loading results...'
                            : progress?.current_file
                              ? `Parsing: ${progress.current_file}`
                              : 'Starting parse job...'}
                        </span>
                        <span className="font-medium">
                          {progress?.files_parsed ?? 0} / {progress?.total_files ?? 0} files
                          {etaSeconds != null && (
                            <span className="text-muted-foreground font-normal ml-2">
                              — ~{formatDuration(etaSeconds)} remaining
                            </span>
                          )}
                        </span>
                      </div>
                      <Progress
                        value={
                          progress?.total_files
                            ? ((progress.files_parsed ?? 0) / progress.total_files) * 100
                            : 0
                        }
                        className="h-3"
                      />
                      <p className="text-xs text-muted-foreground text-center">
                        {isFetchingResult
                          ? 'Downloading parsed results...'
                          : `Processing ${progress?.total_files ?? 0} Excel file(s) from SharePoint`}
                      </p>
                    </div>
                  )}

                  {!isPolling && parseResult && (
                    <>
                      {/* Summary */}
                      <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-center">
                      <p className="text-2xl font-bold text-primary">{totalScenarios}</p>
                      <p className="text-sm text-muted-foreground">Test Scenarios</p>
                    </div>
                    <div className="p-4 rounded-lg bg-info/5 border border-info/20 text-center">
                      <p className="text-2xl font-bold text-info">{totalSteps}</p>
                      <p className="text-sm text-muted-foreground">Test Steps</p>
                    </div>
                    <div className="p-4 rounded-lg bg-success/5 border border-success/20 text-center">
                      <p className="text-2xl font-bold text-success">{totalTeams}</p>
                      <p className="text-sm text-muted-foreground">Test Teams</p>
                    </div>
                    <div className="p-4 rounded-lg bg-warning/5 border border-warning/20 text-center">
                      <p className="text-2xl font-bold text-warning">{parseResult?.metadata.total_user_mappings ?? 0}</p>
                      <p className="text-sm text-muted-foreground">User Mappings</p>
                    </div>
                  </div>

                  {/* Per-file parse errors */}
                  {(parseResult?.errors?.length ?? 0) > 0 && (
                    <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                        <span className="font-medium text-sm text-destructive">
                          {parseResult!.errors!.length} file(s) failed to parse
                        </span>
                      </div>
                      <ul className="space-y-1 ml-6">
                        {parseResult!.errors!.map((err, i) => (
                          <li key={i} className="text-sm">
                            <span className="font-medium">{err.file}</span>
                            <span className="text-muted-foreground ml-1">— {err.error}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-muted-foreground ml-6">
                        Fix the file in SharePoint, then click <strong>Reparse</strong> to retry.
                      </p>
                    </div>
                  )}

                  {/* Failed batches banner — DLQ retry */}
                  {parseResult && Number((parseResult.metadata as Record<string, unknown>).failed_batches) > 0 && (
                    <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-destructive" />
                          <span className="font-medium text-sm text-destructive">
                            {String((parseResult.metadata as Record<string, unknown>).failed_batches)} batch(es) failed to process
                          </span>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleReparse} disabled={isReparsing} className="gap-2">
                          <RefreshCw className={cn("h-4 w-4", isReparsing && "animate-spin")} />
                          Retry
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Some file batches couldn't be processed. Click <strong>Retry</strong> to reparse all files.
                      </p>
                    </div>
                  )}

                  {/* Per-file sanity warnings */}
                  {(parseResult?.warnings?.length ?? 0) > 0 && (
                    <div className="p-4 rounded-lg bg-warning/5 border border-warning/20 space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-warning" />
                        <span className="font-medium text-sm text-warning">
                          {parseResult!.warnings!.length} workbook sanity warning(s)
                        </span>
                      </div>
                      <ul className="space-y-1 ml-6">
                        {parseResult!.warnings!.map((item, i) => (
                          <li key={i} className="text-sm">
                            <span className="font-medium">{item.file}</span>
                            <span className="text-muted-foreground ml-1">— {item.warning}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-muted-foreground ml-6">
                        You can continue, but standardizing worksheet names is recommended.
                      </p>
                    </div>
                  )}

                  {/* Non-new status warnings */}
                  {nonNewAnalysis.totalNonNew > 0 && (
                    <Collapsible open={warningsOpen} onOpenChange={setWarningsOpen}>
                      <div className="p-4 rounded-lg bg-warning/5 border border-warning/20 space-y-2">
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-warning" />
                            <span className="font-medium text-sm text-warning">
                              {nonNewAnalysis.totalNonNew} step(s) across {nonNewAnalysis.affectedScenarios.length} scenario(s) have pre-existing statuses
                            </span>
                            <ChevronDown className="h-4 w-4 text-warning ml-auto" />
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-2 space-y-2">
                            <ul className="space-y-1 ml-6">
                              {nonNewAnalysis.affectedScenarios.map((s) => (
                                <li key={s.index} className="text-sm">
                                  <span className="font-medium">{s.scenarioName}</span>
                                  <span className="text-muted-foreground ml-1">
                                    — {s.nonNewCount} of {s.totalSteps} steps
                                    ({Object.entries(s.byStatus).map(([status, count]) => `${count} ${getStatusConfig(status).shortLabel.toLowerCase()}`).join(', ')})
                                  </span>
                                </li>
                              ))}
                            </ul>
                            <p className="text-sm text-muted-foreground ml-6">
                              For a new project, all steps are expected to have &apos;New&apos; status. Existing statuses will be preserved.
                            </p>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Scenario list */}
                  {(() => {
                    const scenarioTotalPages = Math.ceil(sortedScenarios.length / SCENARIO_PAGE_SIZE);
                    const scenarioSafePage = Math.max(1, Math.min(scenarioPage, scenarioTotalPages));
                    const scenarioStartIdx = (scenarioSafePage - 1) * SCENARIO_PAGE_SIZE;
                    const pageScenarios = sortedScenarios.slice(scenarioStartIdx, scenarioStartIdx + SCENARIO_PAGE_SIZE);

                    return (
                      <>
                        {scenarioTotalPages > 1 && (
                          <p className="text-sm text-muted-foreground">
                            Showing {scenarioStartIdx + 1}–{Math.min(scenarioStartIdx + SCENARIO_PAGE_SIZE, sortedScenarios.length)} of {sortedScenarios.length} scenarios
                          </p>
                        )}
                        <div className={cn(
                          "space-y-3 transition-opacity duration-200",
                          isReparsing && "opacity-50 pointer-events-none"
                        )}>
                          {pageScenarios.map(({ scenario, originalIndex }) => {
                            const id = String(originalIndex);
                            const scenarioNonNew = nonNewAnalysis.scenarioDetails[originalIndex];
                            return (
                              <Collapsible
                                key={id}
                                open={expandedFiles.includes(id)}
                                onOpenChange={() => toggleFileExpansion(id)}
                              >
                                <CollapsibleTrigger className="w-full">
                                  <div className={cn(
                                    "flex items-center gap-3 p-4 rounded-lg transition-colors",
                                    scenarioNonNew
                                      ? "bg-warning/5 border border-warning/20 hover:bg-warning/10"
                                      : "bg-muted/50 border border-border hover:bg-muted/80"
                                  )}>
                                    {expandedFiles.includes(id) ? (
                                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                    )}
                                    <FileSpreadsheet className="h-5 w-5 text-success" />
                                    <span className="font-medium flex-1 text-left">
                                      {scenario.name}
                                    </span>
                                    {scenario.domain && scenario.domain !== 'Unknown' && (
                                      <Badge variant="outline" className="mr-2">{scenario.domain}</Badge>
                                    )}
                                    <Badge variant="secondary">{scenario.steps} steps</Badge>
                                    {scenarioNonNew && (
                                      <Badge variant="outline" className="gap-1 bg-warning/10 text-warning border-warning/30">
                                        <AlertTriangle className="h-3 w-3" />
                                        {scenarioNonNew.nonNewCount} non-new
                                      </Badge>
                                    )}
                                    {!scenario.test_script?.length && (
                                      <Badge variant="destructive" className="gap-1">
                                        <AlertCircle className="h-3 w-3" />
                                        No steps
                                      </Badge>
                                    )}
                                  </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="ml-12 mt-2 space-y-2">
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                                      <span className="text-sm text-muted-foreground">File</span>
                                      <span className="text-sm truncate ml-4">{scenario.name}</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                                      <span className="text-sm text-muted-foreground">Pass Rate</span>
                                      <Badge variant={scenario.pass_rate >= 0.8 ? "default" : "secondary"}>
                                        {(scenario.pass_rate * 100).toFixed(0)}%
                                      </Badge>
                                    </div>
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                                      <span className="text-sm text-muted-foreground">Status Breakdown</span>
                                      <div className="flex gap-1.5 flex-wrap">
                                        {scenario.pass_count > 0 && (
                                          <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/30">
                                            {scenario.pass_count} pass
                                          </Badge>
                                        )}
                                        {scenario.fail_count > 0 && (
                                          <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/30">
                                            {scenario.fail_count} fail
                                          </Badge>
                                        )}
                                        {(scenario.retest_count ?? 0) > 0 && (
                                          <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                                            {scenario.retest_count} retest
                                          </Badge>
                                        )}
                                        {(scenario.ongoing_count ?? 0) > 0 && (
                                          <Badge variant="outline" className="text-xs bg-info/10 text-info border-info/30">
                                            {scenario.ongoing_count} ongoing
                                          </Badge>
                                        )}
                                        {(scenario.blocked_count ?? 0) > 0 && (
                                          <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">
                                            {scenario.blocked_count} blocked
                                          </Badge>
                                        )}
                                        {(scenario.not_executed_count ?? 0) > 0 && (
                                          <Badge variant="outline" className="text-xs">
                                            {scenario.not_executed_count} not exec
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    {scenarioNonNew && (
                                      <div className="flex items-center justify-between p-3 rounded-lg bg-warning/5 border border-warning/20">
                                        <span className="text-sm text-warning flex items-center gap-1.5">
                                          <AlertTriangle className="h-3.5 w-3.5" />
                                          Non-new statuses
                                        </span>
                                        <span className="text-sm font-medium text-warning">
                                          {scenarioNonNew.nonNewCount} of {scenarioNonNew.totalSteps} steps
                                        </span>
                                      </div>
                                    )}
                                    {(scenario.testers ?? []).length > 0 && (
                                      <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                                        <span className="text-sm text-muted-foreground">Testers</span>
                                        <div className="flex gap-1 flex-wrap">
                                          {(scenario.testers ?? []).map(t => (
                                            <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {/* Test Steps Accordion */}
                                    {(scenario.test_script ?? []).length > 0 && (
                                      <div className="mt-3 pt-3 border-t">
                                        <p className="text-sm font-medium mb-2">
                                          Test Steps ({scenario.test_script!.length})
                                        </p>
                                        <Accordion type="single" collapsible className="space-y-0">
                                          {scenario.test_script!.map((step, stepIdx) => {
                                            const stepNum = step.STEP || step.Step || step.step || String(stepIdx + 1);
                                            const action = step.Action || step.ACTION || step.action || "";
                                            const status = step.Status || step.STATUS || step.status || "";
                                            const visibleFields = getVisibleFields(step);

                                            return (
                                              <AccordionItem
                                                key={stepIdx}
                                                value={`step-${stepIdx}`}
                                                className="border-b-0"
                                              >
                                                <AccordionTrigger className="py-2 px-3 text-sm hover:no-underline hover:bg-muted/50 rounded-md gap-2">
                                                  <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
                                                    <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded shrink-0">
                                                      #{stepNum}
                                                    </span>
                                                    <span className="truncate text-muted-foreground">
                                                      {action ? formatMultilinePreview(action) : "\u2014"}
                                                    </span>
                                                  </div>
                                                  {status && (
                                                    <Badge
                                                      variant="outline"
                                                      className={cn("text-xs shrink-0 ml-2", getStepStatusColor(status))}
                                                    >
                                                      {getStatusConfig(status).label}
                                                    </Badge>
                                                  )}
                                                </AccordionTrigger>
                                                <AccordionContent className="px-3 pb-3 pt-1">
                                                  {visibleFields.length > 0 ? (
                                                    <div className="grid gap-2">
                                                      {visibleFields.map(([key, val]) => (
                                                        <div key={key} className="grid grid-cols-[140px_1fr] gap-2 text-sm">
                                                          <span className="text-muted-foreground font-medium">
                                                            {cleanColumnName(key)}
                                                          </span>
                                                          <span className="whitespace-pre-line break-words">
                                                            {val}
                                                          </span>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <p className="text-sm text-muted-foreground italic">
                                                      No additional fields
                                                    </p>
                                                  )}
                                                </AccordionContent>
                                              </AccordionItem>
                                            );
                                          })}
                                        </Accordion>
                                      </div>
                                    )}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            );
                          })}
                        </div>
                        {scenarioTotalPages > 1 && (
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious
                                  onClick={() => setScenarioPage(p => Math.max(1, p - 1))}
                                  className={cn(scenarioSafePage <= 1 && "pointer-events-none opacity-50")}
                                />
                              </PaginationItem>
                              {Array.from({ length: scenarioTotalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === scenarioTotalPages || Math.abs(p - scenarioSafePage) <= 1)
                                .map((p, idx, arr) => (
                                  <Fragment key={p}>
                                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                                      <PaginationItem><PaginationEllipsis /></PaginationItem>
                                    )}
                                    <PaginationItem>
                                      <PaginationLink
                                        isActive={p === scenarioSafePage}
                                        onClick={() => setScenarioPage(p)}
                                      >
                                        {p}
                                      </PaginationLink>
                                    </PaginationItem>
                                  </Fragment>
                                ))}
                              <PaginationItem>
                                <PaginationNext
                                  onClick={() => setScenarioPage(p => Math.min(scenarioTotalPages, p + 1))}
                                  className={cn(scenarioSafePage >= scenarioTotalPages && "pointer-events-none opacity-50")}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        )}
                      </>
                    );
                  })()}
                    </>
                  )}
                </CardContent>
              </>
            )}

            {/* Step 4: Map Testers */}
            {currentStep === 4 && (
              <TesterMappingStep
                testerMappings={testerMappings}
                mappingSearch={mappingSearch}
                expandedSubdomains={expandedSubdomains}
                expandedMappingFiles={expandedMappingFiles}
                scenarioStepKeys={scenarioStepKeysState}
                selectedTestFiles={selectedTestFiles}
                parseResult={parseResult}
                isTestersLoading={isTestersLoading}
                isTestersError={isTestersError}
                isResyncingMappings={isResyncingMappings}
                parseExcelIsPending={parseExcel.isPending}
                isPolling={isPolling}
                cognitoUsers={cognitoUsers}
                onMappingSearchChange={setMappingSearch}
                onSubdomainToggle={handleSubdomainToggle}
                onFileToggle={handleFileToggle}
                onResolve={handleResolve}
                onRemove={removeTestPerson}
                onAddUser={handleAddCognitoUser}
                onAssignToAll={handleAssignToAll}
                onResync={handleResyncMappings}
                onRetryTesters={() => refetchTesters()}
                mappingPage={mappingPage}
                onMappingPageChange={setMappingPage}
              />
            )}

          </Card>
          )}

          {/* Navigation Guard Dialog */}
          <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Leave project wizard?</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved progress. Your draft will be saved automatically so you
                  can resume later, or you can discard it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    clearDraft();
                    setShowLeaveDialog(false);
                    navigate('/admin/projects');
                  }}
                >
                  Leave &amp; Discard
                </Button>
                <Button
                  onClick={() => {
                    saveDraft({
                      currentStep, projectName, projectNumber, testingPhase,
                      projectDeadline, ictResponsible, selectedTestFiles,
                      selectedMappingFile, parseResult, testerMappings,
                      jobId: effectiveJobId ?? undefined,
                    });
                    setShowLeaveDialog(false);
                    navigate('/admin/projects');
                  }}
                >
                  Leave &amp; Save Draft
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Navigation Buttons */}
          {!showDraftPrompt && (
          <div className="flex justify-between">
            {currentStep === 1 ? (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isLoading || isReparsing || isResyncingMappings}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={isLoading || isReparsing || isResyncingMappings || createProject.isPending || parseExcel.isPending || (currentStep === 1 && !isStep1Complete) || (currentStep === 3 && isPolling) || (currentStep === 4 && mappingMismatch)}
              className="gap-2 min-w-[140px]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : currentStep === 4 ? (
                <>
                  <Check className="h-4 w-4" />
                  Create Project
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
          )}
        </div>
      </main>

      {/* Floating action buttons */}
      <div className="fixed bottom-24 right-6 z-50 flex flex-col gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={scrollToTop}
          className={cn(
            "rounded-full shadow-md transition-opacity duration-200",
            showScrollTopButton ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        {(currentStep === 3 || currentStep === 4) && (
          <Button
            variant="outline"
            size="icon"
            onClick={collapseAll}
            className="rounded-full shadow-md"
            aria-label="Collapse all"
          >
            <ChevronsDownUp className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="outline"
          size="icon"
          onClick={scrollToBottom}
          className={cn(
            "rounded-full shadow-md transition-opacity duration-200",
            showScrollButton ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          aria-label="Scroll to bottom"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default NewProject;
